import { useEffect, useRef, useState } from 'react';
import { getDefaultMemberCountry } from '../../admin/settings/settingsData';
import { getReceptionistBranch } from '../../../services/gym/branchContext';
import '../../owner/ownerPages.css';
import '../../owner/subscriptions/subscriptions.css';
import '../../admin/members/members.css';
import '../sales.css';

const emptyForm = {
  photo: null,
  photoPreview: null,
  firstName: '',
  lastName: '',
  mobile: '',
  alternateMobile: '',
  landline: '',
  cnic: '',
  email: '',
  dob: '',
  gender: 'male',
  addressLine1: '',
  city: '',
  state: '',
  country: getDefaultMemberCountry(),
  zipCode: '',
  createdAt: '',
  branch: '',
};

function ReceptionistMemberForm({ initialData, onSubmit, onCancel, submitLabel }) {
  const fileInputRef = useRef(null);
  const isEdit = Boolean(initialData?.firstName || initialData?.name);
  const assignedBranch = getReceptionistBranch() || '';
  const [form, setForm] = useState({
    ...emptyForm,
    country: getDefaultMemberCountry(),
    ...initialData,
    // New members always inherit the receptionist's branch; edits keep existing
    branch: isEdit
      ? (initialData?.branch || assignedBranch)
      : (assignedBranch || initialData?.branch || ''),
  });

  useEffect(() => {
    if (isEdit) return undefined;

    const syncCountry = () => {
      setForm((prev) => ({ ...prev, country: getDefaultMemberCountry() }));
    };

    syncCountry();
    window.addEventListener('gym-admin-settings-updated', syncCountry);
    return () => window.removeEventListener('gym-admin-settings-updated', syncCountry);
  }, [isEdit]);

  useEffect(() => {
    if (isEdit) return;
    const assigned = getReceptionistBranch();
    if (!assigned) return;
    setForm((prev) => (prev.branch === assigned ? prev : { ...prev, branch: assigned }));
  }, [isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({ ...prev, photo: file, photoPreview: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setForm((prev) => ({ ...prev, photo: null, photoPreview: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const name = String(form.firstName || '').trim();
    const phone = String(form.mobile || '').trim();
    if (!name) {
      e.target.querySelector('#firstName')?.focus();
      return;
    }
    if (!phone) {
      e.target.querySelector('#mobile')?.focus();
      return;
    }
    const branch = isEdit
      ? (form.branch || getReceptionistBranch() || '')
      : (getReceptionistBranch() || form.branch || '');
    onSubmit?.({ ...form, branch });
  };

  return (
    <form className="subscription-form" onSubmit={handleSubmit} noValidate>
      <div className="member-form-grid">
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="firstName">First Name *</label>
          <input id="firstName" name="firstName" className="owner-form-input" value={form.firstName} onChange={handleChange} required />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="lastName">Last Name</label>
          <input id="lastName" name="lastName" className="owner-form-input" value={form.lastName} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="mobile">Mobile No *</label>
          <input id="mobile" name="mobile" type="tel" className="owner-form-input" value={form.mobile} onChange={handleChange} required />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="alternateMobile">Alternate Mobile Number</label>
          <input id="alternateMobile" name="alternateMobile" type="tel" className="owner-form-input" value={form.alternateMobile} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="landline">Landline</label>
          <input id="landline" name="landline" type="tel" className="owner-form-input" value={form.landline} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="cnic">CNIC</label>
          <input
            id="cnic"
            name="cnic"
            className="owner-form-input"
            value={form.cnic}
            onChange={handleChange}
            placeholder="e.g. 42101-1234567-1"
            inputMode="numeric"
          />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="email">Email</label>
          <input id="email" name="email" type="email" className="owner-form-input" value={form.email} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="dob">Date of Birth</label>
          <input id="dob" name="dob" type="date" className="owner-form-input" value={form.dob} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="gender">Gender</label>
          <select id="gender" name="gender" className="owner-form-select" value={form.gender} onChange={handleChange}>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="owner-form-group member-form-full">
          <label className="owner-form-label" htmlFor="addressLine1">Address Line 1</label>
          <input id="addressLine1" name="addressLine1" className="owner-form-input" value={form.addressLine1} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="city">City</label>
          <input id="city" name="city" className="owner-form-input" value={form.city} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="state">State</label>
          <input id="state" name="state" className="owner-form-input" value={form.state} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="country">Country</label>
          <input
            id="country"
            name="country"
            className="owner-form-input rec-member-country-readonly"
            value={form.country}
            readOnly
            aria-readonly="true"
          />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="zipCode">Zip Code</label>
          <input id="zipCode" name="zipCode" className="owner-form-input" value={form.zipCode} onChange={handleChange} />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="createdAt">Date Created</label>
          <input
            id="createdAt"
            name="createdAt"
            type="date"
            className="owner-form-input"
            value={form.createdAt || ''}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="rec-member-photo-upload member-photo-upload-end">
        <label className="owner-form-label">Profile Photo</label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="member-file-input"
          onChange={handlePhotoSelect}
        />
        <div className="rec-member-photo-actions">
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => fileInputRef.current?.click()}>
            Upload Photo
          </button>
          {form.photoPreview && (
            <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={handleRemovePhoto}>
              Remove Photo
            </button>
          )}
        </div>
        {form.photo?.name && (
          <p className="rec-member-photo-filename">{form.photo.name}</p>
        )}
      </div>

      <div className="subscription-form-actions">
        <button type="submit" className="owner-btn owner-btn-primary">{submitLabel}</button>
        {onCancel && (
          <button type="button" className="owner-btn owner-btn-secondary" onClick={onCancel}>Cancel</button>
        )}
      </div>
    </form>
  );
}

export default ReceptionistMemberForm;
